<template>
    <div class="embed-course-layout" :class="{ 'theme-dark': cssVars['--is-dark-theme'] === '1' }" :style="cssVars">
        <div class="bg-decoration shape-1"></div>
        <div class="bg-decoration shape-2"></div>

        <div v-if="error" class="error-overlay glass-effect">
            <i class="fas fa-exclamation-circle error-icon"></i>
            <h2>Ops!</h2>
            <p>{{ error }}</p>
        </div>

        <!-- Loader -->
        <div v-else-if="loading || checkingAuth" class="loader-overlay glass-effect">
            <div class="spinner"></div>
            <p>Carregando sala de aula...</p>
        </div>

        <template v-else>
            <EmbedHeader :logo="courseData.customization && courseData.customization.logo"
                :courseName="courseData.name" />

            <!-- Container corpo (sidebar + conteudo) -->
            <div class="embed-body-wrapper">
                <!-- Sidebar de Disciplinas -->
                <EmbedSidebar :disciplines="courseData.disciplines" :activeDiscipline="activeDiscipline"
                    :progressData="disciplineProgress" @select="selectDiscipline" />

                <!-- Container principal à direita da sidebar -->
                <div class="embed-main-wrapper">
                    <!-- Conteúdo -->
                    <main class="content-area">
                        <div v-if="!activeDiscipline" class="empty-state">
                            <div class="empty-state-content">
                                <i class="fas fa-arrow-left pulse-icon"></i>
                                <h3>Selecione uma disciplina no menu ao lado</h3>
                                <p>Explore as vídeo-aulas e materiais interativos.</p>
                            </div>
                        </div>

                        <div v-else class="content-tabs-container">
                            <!-- Abas -->
                            <div class="tabs">
                                <button :class="['tab-btn', { active: activeTab === 'video' }]"
                                    @click="activeTab = 'video'">
                                    <i class="fas fa-play-circle"></i> Sala de Aula
                                </button>
                                <button :class="['tab-btn', { active: activeTab === 'simulado' }]"
                                    @click="activeTab = 'simulado'">
                                    <i class="fas fa-clipboard-check"></i> Simulados
                                </button>
                            </div>

                            <!-- Conteúdo da Aba -->
                            <div class="tab-content">
                                <div v-if="loadingContent" class="content-loader glass-effect">
                                    <div class="spinner"></div>
                                </div>

                                <div v-else-if="activeTab === 'video'" class="grid-cards">
                                    <div v-if="!videos.length" class="no-content">Nenhuma vídeo-aula disponível.</div>
                                    <div v-for="(vid, index) in videos" :key="'vid-' + vid.codigo_aula"
                                        class="modern-card video-card" @click="openLesson(vid)">
                                        <div class="card-thumbnail">
                                            <div class="thumbnail-overlay">
                                                <i class="fas fa-play"></i>
                                            </div>
                                            <span class="lesson-badge">Aula {{ index + 1 }}</span>
                                        </div>
                                        <div class="card-info">
                                            <h4>
                                                <i v-if="completedItemIds.includes('lesson_' + vid.codigo_aula)"
                                                    class="fas fa-check-circle text-success"
                                                    style="margin-right: 5px;"></i>
                                                {{ (vid.nome_arquivo || `Aula ${vid.ordem_aula}`).replace(/\.pdf$/i, '')
                                                }}
                                            </h4>
                                        </div>
                                    </div>
                                </div>

                                <div v-else-if="activeTab === 'simulado'" class="grid-cards">
                                    <div v-if="!completedSimulados.length" class="no-content">Nenhum simulado concluído ainda.</div>
                                    <div v-for="sim in completedSimulados" :key="'sim-' + sim.codigo_aula"
                                        class="modern-card simulado-card" @click="viewSimulado(sim)">
                                        <div class="card-thumbnail simulado-thumbnail">
                                            <i class="fas fa-clipboard-list"></i>
                                        </div>
                                        <div class="card-info">
                                            <h4>
                                                <i class="fas fa-check-circle text-success" style="margin-right: 5px;"></i>
                                                {{ getSimuladoName(sim) }}
                                            </h4>
                                            <div class="simulado-score-details">
                                                <div class="simulado-main-score">
                                                    Nota Geral: <strong>{{ studentProgress[sim.codigo_aula]?.score !== undefined && studentProgress[sim.codigo_aula]?.score !== null ? studentProgress[sim.codigo_aula].score : 'N/A' }}</strong>
                                                </div>
                                                <div v-if="simuladoAttempts[sim.codigo_aula] && simuladoAttempts[sim.codigo_aula].length > 0" class="simulado-history">
                                                    <span class="history-title">Histórico de Notas (máx. 3):</span>
                                                    <ul class="history-list">
                                                        <li v-for="(attempt, aIdx) in simuladoAttempts[sim.codigo_aula]" :key="'att-' + aIdx" class="history-item">
                                                            Nota: <strong>{{ attempt.score }}</strong> <span class="attempt-date">({{ formatDate(attempt.created_at) }})</span>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                            <button class="refazer-btn">
                                                <i class="fas fa-redo"></i> Refazer Simulado
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>

            <EmbedFooter :instituicaoNome="courseData.nome_instituicao" />
        </template>

        <!-- Modal View -->
        <EmbedLessonModal
            :isOpen="modalOpen"
            :modalType="modalType"
            :activeModalTab="activeModalTab"
            :currentLesson="currentLesson"
            :currentMediaVideo="currentMediaVideo"
            :currentMediaPdf="currentMediaPdf"
            :loadingPdf="loadingPdf"
            :loadingLesson="loadingLesson"
            :questoesSimulado="questoesSimulado"
            :savedAnswers="savedSimuladoAnswers"
            @close="closeModal"
            @set-tab="setModalTab"
            @progress-update="handleProgressUpdate"
            @save-answers="handleSaveSimuladoAnswers"
        />

    </div>
</template>

<script>
import axios from 'axios';
import EmbedHeader from '@/components/EmbedHeader/EmbedHeader.vue';
import EmbedSidebar from '@/components/EmbedSidebar/EmbedSidebar.vue';
import EmbedLessonModal from '@/components/EmbedLessonModal/EmbedLessonModal.vue';
import EmbedFooter from '@/components/EmbedFooter/EmbedFooter.vue';

const publicRequest = axios.create({
    baseURL: '/api',
    withCredentials: true
});

const publicRequestV2 = axios.create({
    baseURL: '/api/v2',
    withCredentials: true
});

export default {
    name: 'EmbedCourse',
    components: {
        EmbedHeader,
        EmbedSidebar,
        EmbedLessonModal,
        EmbedFooter
    },
    data() {
        return {
            loading: true,
            checkingAuth: true,
            error: null,
            courseData: null,
            activeDiscipline: null,
            activeTab: 'video',
            loadingContent: false,

            videos: [],
            simulados: [],

            studentData: null,
            studentProgress: {},
            authTimeout: null,
            parentUrl: '',

            modalOpen: false,
            modalType: '', // 'lesson', 'simulado'
            activeModalTab: 'video', // 'video', 'pdf'
            currentLesson: null,
            currentMediaVideo: null,
            currentMediaPdf: null,
            loadingPdf: false,
            questoesSimulado: [],
            completedItemIds: [],
            disciplineProgress: {},
            currentSimulado: null,
            loadingLesson: false,
            savedSimuladoAnswers: {},
            simuladoAttempts: {}
        };
    },
    computed: {
        cssVars() {
            if (!this.courseData || !this.courseData.customization) return {};
            const bg = this.courseData.customization.background_color || '#09105D';

            // Calculate a contrast text color based on background luminance
            let textColor = '#09105D';
            let isDark = false;
            if (bg.startsWith('#') && (bg.length === 7 || bg.length === 4)) {
                let hex = bg.replace('#', '');
                if (hex.length === 3) {
                    hex = hex.split('').map(c => c + c).join('');
                }
                const r = parseInt(hex.substr(0, 2), 16);
                const g = parseInt(hex.substr(2, 2), 16);
                const b = parseInt(hex.substr(4, 2), 16);
                const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
                if (luminance <= 0.5) {
                    textColor = '#ffffff';
                    isDark = true;
                }
            }

            return {
                '--primary-color': this.courseData.customization.header_color || '#09105D',
                '--bg-color': bg,
                '--text-color': textColor,
                '--is-dark-theme': isDark ? '1' : '0'
            };
        },
        completedSimulados() {
            if (!this.simulados || !this.studentProgress) return [];
            return this.simulados.filter(sim => {
                const code = String(sim.codigo_aula);
                const progressEntry = this.studentProgress[code];
                const hasAttempts = this.simuladoAttempts && this.simuladoAttempts[code] && this.simuladoAttempts[code].length > 0;
                return (progressEntry && progressEntry.completed) || hasAttempts;
            });
        }
    },
    methods: {
        setupAuthListener() {
            if (window.self === window.top) {
                this.error = 'Autenticação necessária. Por favor, acesse o curso pelo portal da sua instituição.';
                this.checkingAuth = false;
                return;
            }

            window.addEventListener('message', this.handlePostMessage);

            if (window.__pendingStudentAuth) {
                const pending = window.__pendingStudentAuth;
                window.__pendingStudentAuth = null;

                this.processAuthMessage(pending.data, pending.origin, pending.source, pending.referrer);
            }

            this.authTimeout = setTimeout(() => {
                if (!this.studentData) {
                    this.error = 'Autenticação necessária. Por favor, acesse o curso pelo portal da sua instituição.';
                    this.checkingAuth = false;
                }
            }, 3000);
        },
        handlePostMessage(event) {
            this.processAuthMessage(event.data, event.origin, event.source, document.referrer);
        },
        processAuthMessage(data, origin, source, referrer) {
            if (source !== window.parent) return;

            if (referrer) {
                try {
                    const referrerOrigin = new URL(referrer).origin;
                    if (origin !== referrerOrigin && origin !== window.location.origin) {
                        return;
                    }
                } catch (e) {
                    console.warn("Invalid document.referrer URL:", e);
                }
            }

            if (data && data.type === 'STUDENT_AUTH') {
                if (this.authTimeout) clearTimeout(this.authTimeout);
                this.studentData = {
                    student_id: data.student_id,
                    student_name: data.student_name,
                    student_email: data.student_email
                };
                this.checkingAuth = false;
                this.loadStudentProgress();
            }
        },
        async loadStudentProgress() {
            if (!this.studentData || !this.courseData) return;
            try {
                const hash = this.$route.params.hash;
                const res = await publicRequestV2.get(`/embed/${hash}/progress/${this.studentData.student_id}`, {
                    params: { parentUrl: this.parentUrl }
                });
                const progressMap = {};
                const completedIds = [];
                const progressList = res.data.progress || [];
                const attemptsData = res.data.attempts || {};
                progressList.forEach(p => {
                    const code = String(parseInt(p.lesson_code)) || String(p.lesson_code);
                    progressMap[code] = p;
                    if (p.completed) {
                        completedIds.push(p.lesson_type + '_' + code);
                    }
                });
                this.studentProgress = progressMap;
                this.completedItemIds = completedIds;
                this.simuladoAttempts = attemptsData;
                this.recalcCurrentDisciplineProgress();
            } catch (err) {
                console.error("Erro ao carregar progresso", err);
            }
        },
        async saveProgress(lessonCode, lessonType, progress, completed, score = null) {
            if (!this.studentData || !this.courseData) return;
            try {
                const hash = this.$route.params.hash;
                await publicRequestV2.post(`/embed/${hash}/progress`, {
                    student_id: this.studentData.student_id,
                    student_name: this.studentData.student_name,
                    student_email: this.studentData.student_email,
                    lesson_code: lessonCode,
                    lesson_type: lessonType,
                    progress,
                    completed,
                    score,
                    parentUrl: this.parentUrl
                });

                this.$set(this.studentProgress, lessonCode, {
                    lesson_code: lessonCode,
                    lesson_type: lessonType,
                    progress,
                    completed,
                    score
                });
            } catch (err) {
                console.error("Erro ao salvar progresso", err);
            }
        },
        handleProgressUpdate(data) {
            const score = data.score !== undefined ? data.score : null;
            this.saveProgress(data.id, data.type, 100, true, score);

            const itemId = data.type + '_' + data.id;
            if (!this.completedItemIds.includes(itemId)) {
                this.completedItemIds.push(itemId);
                this.recalcCurrentDisciplineProgress();
            }
        },
        recalcCurrentDisciplineProgress() {
            if (!this.activeDiscipline) return;

            let total = 0;
            let completed = 0;

            this.videos.forEach(v => {
                total += 1;
                const lessonId = 'lesson_' + v.codigo_aula;
                if (this.completedItemIds.includes(lessonId)) {
                    completed++;
                }
            });

            const pct = total === 0 ? 0 : Math.round((completed / total) * 100);
            this.$set(this.disciplineProgress, this.activeDiscipline, pct);
        },
        async fetchCourseData() {
            const hash = this.$route.params.hash;
            if (!hash) {
                this.error = "Hash do curso não fornecido.";
                this.loading = false;
                return;
            }

            try {
                let parentOrigin = '';
                if (window.self !== window.top) {
                    parentOrigin = document.referrer;
                } else {
                    parentOrigin = window.location.href;
                }

                // Rota pública v2 para dados do curso
                this.parentUrl = parentOrigin;

                const res = await publicRequestV2.get(`/embed/${hash}`, {
                    params: { parentUrl: parentOrigin }
                });
                this.courseData = res.data;

                if (this.courseData.disciplines && this.courseData.disciplines.length > 0) {
                    this.selectDiscipline(this.courseData.disciplines[0].codigo_disciplina);
                }

                if (this.studentData) {
                    this.loadStudentProgress();
                }
            } catch (err) {
                this.error = err.response?.data?.message || "Erro ao carregar a sala de aula.";
                if (this.authTimeout) {
                    clearTimeout(this.authTimeout);
                    this.checkingAuth = false;
                }
            } finally {
                this.loading = false;
            }
        },
        async selectDiscipline(codigo) {
            this.activeDiscipline = codigo;
            this.activeTab = 'video';
            this.loadDisciplineContent(codigo);
        },
        async loadDisciplineContent(codigo) {
            this.loadingContent = true;
            this.videos = [];
            this.simulados = [];

            try {
                const [resVid, resSim] = await Promise.all([
                    publicRequest.post('asas/listarVideoAulas', { codigo_disciplina: codigo }),
                    publicRequest.post('asas/listarSimulado', { codigo_disciplina: codigo })
                ]);

                this.videos = resVid.data || [];
                this.simulados = resSim.data || [];

                this.recalcCurrentDisciplineProgress();
            } catch (err) {
                console.error("Erro ao carregar conteúdo da disciplina:", err);
            } finally {
                this.loadingContent = false;
            }
        },
        async openLesson(lesson) {
            this.currentLesson = lesson;
            this.modalType = 'lesson';
            this.modalOpen = true;
            this.loadingLesson = true;
            this.currentMediaVideo = null;
            this.currentMediaPdf = null;
            this.loadingPdf = false;
            this.questoesSimulado = [];
            this.savedSimuladoAnswers = {};

            try {
                const sim = this.simulados.find(s => s.codigo_aula === lesson.codigo_aula);
                if (sim) {
                    try {
                        const resp = await publicRequest.post('asas/listarQuestoes', {
                            codigo_aula: sim.codigo_aula
                        });
                        this.questoesSimulado = resp.data || [];
                        this.currentSimulado = sim;

                        if (this.studentData && this.questoesSimulado.length > 0) {
                            try {
                                const hash = this.$route.params.hash;
                                const answersResp = await publicRequestV2.get(
                                    `/embed/${hash}/simulado-answers/${this.studentData.student_id}/${sim.codigo_aula}`,
                                    { params: { parentUrl: this.parentUrl } }
                                );
                                if (answersResp.data && answersResp.data.length > 0) {
                                    const answersMap = {};
                                    answersResp.data.forEach(a => {
                                        answersMap[a.question_code] = {
                                            selected_option: a.selected_option,
                                            is_correct: a.is_correct
                                        };
                                    });
                                    this.savedSimuladoAnswers = answersMap;
                                }
                            } catch(e) {
                                console.warn("Erro ao carregar respostas salvas do simulado:", e);
                            }
                        }
                    } catch(e) {
                        console.warn("Erro ao carregar simulado para aula:", e);
                    }
                } else {
                    this.currentSimulado = null;
                }

                if (lesson.url_video_aula) {
                    this.activeModalTab = 'video';
                    const urlParts = lesson.url_video_aula.split("/");
                    const vimeoId = urlParts[urlParts.length - 1];
                    this.currentMediaVideo = 'https://player.vimeo.com/video/' + vimeoId;
                } else if (lesson.hash_arq_pdf_aula) {
                    this.activeModalTab = 'pdf';
                    await this.loadLessonPdf(lesson);
                } else if (this.questoesSimulado.length > 0) {
                    this.activeModalTab = 'simulado';
                }
            } finally {
                this.loadingLesson = false;
            }
        },
        async setModalTab(tab) {
            this.activeModalTab = tab;
            if (tab === 'pdf' && !this.currentMediaPdf) {
                await this.loadLessonPdf(this.currentLesson);
            }
        },
        async loadLessonPdf(lesson) {
            this.loadingPdf = true;
            try {
                const resp = await publicRequest.post("asas/prepararDownloadAula", {
                    ano_mes_dia_arq: lesson.amd_arq_pdf_aula,
                    hash_arquivo: lesson.hash_arq_pdf_aula
                });
                this.currentMediaPdf = "/api/arquivo/download?hash=" + resp.data.hash;
            } catch (err) {
                console.error("Erro ao carregar PDF:", err);
            } finally {
                this.loadingPdf = false;
            }
        },
        closeModal() {
            this.modalOpen = false;
            this.currentLesson = null;
            this.currentSimulado = null;
            this.currentMediaVideo = null;
            this.currentMediaPdf = null;
            this.questoesSimulado = [];
            this.savedSimuladoAnswers = {};
        },
        async handleSaveSimuladoAnswers(payload) {
            if (!this.studentData || !this.courseData) return;
            try {
                const hash = this.$route.params.hash;
                await publicRequestV2.post(`/embed/${hash}/simulado-answers`, {
                    student_id: this.studentData.student_id,
                    lesson_code: String(payload.lesson_code),
                    answers: payload.answers,
                    score: payload.score,
                    parentUrl: this.parentUrl
                });
            } catch (err) {
                console.error("Erro ao salvar respostas do simulado:", err);
            }
        },
        async viewSimulado(sim) {
            const lesson = this.videos.find(v => v.codigo_aula === sim.codigo_aula);
            if (lesson) {
                await this.openLesson(lesson);
                this.activeModalTab = 'simulado';
                return;
            }
            try {
                const resp = await publicRequest.post('asas/listarQuestoes', {
                    codigo_aula: sim.codigo_aula
                });
                this.questoesSimulado = resp.data || [];
                this.currentSimulado = sim;
                this.modalType = 'simulado';
                this.modalOpen = true;
            } catch(e) {
                alert("Erro ao abrir simulado");
            }
        },
        formatDate(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        },
        getSimuladoName(sim) {
            const lesson = this.videos.find(v => v.codigo_aula === sim.codigo_aula);
            if (lesson) {
                return (lesson.nome_arquivo || `Aula ${lesson.ordem_aula}`).replace(/\.pdf$/i, '');
            }
            return `Aula ${sim.ordem_aula}`;
        }
    },
    mounted() {
        if (window.self === window.top) {
            this.error = 'Autenticação necessária. Por favor, acesse o curso pelo portal da sua instituição.';
            this.checkingAuth = false;
            this.loading = false;
            document.documentElement.classList.add('embed-mode');
            document.body.classList.add('embed-mode');
            const appEl = document.getElementById('app');
            if (appEl) appEl.classList.add('embed-mode');
            return;
        }

        this.fetchCourseData();
        this.setupAuthListener();
        
        document.documentElement.classList.add('embed-mode');
        document.body.classList.add('embed-mode');
        const appEl = document.getElementById('app');
        if (appEl) appEl.classList.add('embed-mode');
    },
    beforeDestroy() {
        window.removeEventListener('message', this.handlePostMessage);
        if (this.authTimeout) clearTimeout(this.authTimeout);
        document.documentElement.classList.remove('embed-mode');
        document.body.classList.remove('embed-mode');
        const appEl = document.getElementById('app');
        if (appEl) appEl.classList.remove('embed-mode');
    }
}
</script>

<style scoped src="../styles/EmbedCourse.css" />
